import { useState, useEffect } from 'react'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service'; // Importing authService for login
import Dialog from '../components/Dialog';
import { toast } from 'react-toastify';
import { registerNavigationCallback } from '../utils/authUtils';

// Declare global type for temporary OTP userId
declare global {
  interface Window {
    tempOtpUserId?: string;
  }
}

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [username, setUsername] = useState(() => {
    // Initialize username from localStorage if "Remember me" was checked
    return localStorage.getItem('rememberedUsername') || ''
  })
  const [password, setPassword] = useState('')  // Don't initialize from storage for security
  const [rememberMe, setRememberMe] = useState(() => {
    // Initialize rememberMe state from localStorage
    return localStorage.getItem('rememberMe') === 'true'
  })
  const [errors, setErrors] = useState({ username: '', password: '' })
  const navigate = useNavigate(); // Moved navigate here for proper scope
  const [showOtpDialog, setShowOtpDialog] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const [isGeneratingOtp, setIsGeneratingOtp] = useState(false)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  
  // Register navigation callback and check if already authenticated
  useEffect(() => {
    registerNavigationCallback(navigate);
    
    // If already authenticated, redirect to appropriate dashboard
    if (authService.isAuthenticated()) {
      const user = authService.getUser();
      const path = authService.getRolePath(user?.role || '');
      navigate(path, { replace: true });
    }
  }, [navigate]);

  const PASSWORD_REQUIRED_MESSAGE = 'Password is required';

  const validateForm = () => {
    let isValid = true
    const newErrors = { username: '', password: '' }

    // Trim username/email before validation
    const trimmedUsername = username.trim()
    
    if (!trimmedUsername) {
      newErrors.username = 'Email is required'
      isValid = false
    }

    if (!password.trim()) {
      newErrors.password = PASSWORD_REQUIRED_MESSAGE;
      isValid = false;
    }

    setErrors(newErrors)
    return isValid
  }

  // Handle remember me functionality
  const handleRememberMe = (username: string) => {
    if (rememberMe) {
      localStorage.setItem('rememberedUsername', username.trim())
      localStorage.setItem('rememberMe', 'true')
    } else {
      localStorage.removeItem('rememberedUsername')
      localStorage.setItem('rememberMe', 'false')
    }
  }

  // Handle response validation and error setting
  const handleLoginResponse = (response: any) => {
    if (!response?.user) {
      const errorMessage = response?.message || 'An unknown error occurred.'
      setErrors({ ...errors, username: errorMessage, password: errorMessage })
      return false
    }
    if (!response?.accessToken) {
      setErrors({ ...errors, username: 'Authentication failed. Please try again.' })
      return false
    }
    if (!response?.refreshToken) {
      setErrors({ ...errors, username: 'Invalid response from server.' })
      return false
    }
    return true
  }

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoggingIn(true);
    try {
      const trimmedUsername = username.trim().toLowerCase();
      const response = await authService.login({ email: trimmedUsername, password });

      handleRememberMe(username);

      // Check if 2FA is required
      if (response?.requires2FA) {
        if (response?.userId) {
          setUserId(response.userId)
          setShowOtpDialog(true)
          return
        } else {
          // Handle missing userId case
          toast.error('User ID is missing. Please try logging in again.');
          return
        }
      }

      // Validate response and handle errors
      if (!handleLoginResponse(response)) return;

    } catch (error: any) {
      console.error('Login failed:', error)
      const errorMessage = error?.message || 'Login failed. Please check your credentials.'
      toast.error(errorMessage)
    } finally {
      setIsLoggingIn(false);
    }
  }

  const handleGenerateOtp = async () => {
    if (!userId) {
      toast.error('User ID is missing. Please try logging in again.')
      return
    }

    setIsGeneratingOtp(true)
    try {
      const response = await authService.generateOtp(userId)
      if (response?.message) {
        // Store the userId for the OTP verification page
        // We'll use a temporary in-memory variable since we're moving away from sessionStorage
        window.tempOtpUserId = userId;
        navigate('/two-factor-verify', { state: { userId } })
      } else {
        
        toast.error('Failed to generate OTP. Please try again.')
      }
    } catch (error) {
      console.error('OTP generation failed:', error)
      toast.error('Failed to generate OTP. Please try again.')
    } finally {
      setIsGeneratingOtp(false)
      setShowOtpDialog(false)
    }
  }
  
  return (
    <div className="min-h-screen bg-primary relative flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-cover bg-[position:0%_50%] mix-blend-luminosity opacity-[0.08] rotate-180 auth-background"
      />
      <div className="bg-white rounded-card shadow-lg w-full max-w-card p-card-padding pb-card-padding-bottom gap-section-gap relative z-10">
        <div className="flex flex-col items-center mb-8">
          <img src="/ua-canada-logo.png" alt="UA Canada Logo" className="h-12 mb-6" />
          <h2 className="text-heading font-heading text-text-primary text-center mb-2">UA Canada Organizing App</h2>
          <p className="text-text-secondary text-subtext font-normal">Login to your account</p>
        </div>

        <form onSubmit={(e) => {
          e.preventDefault();
          handleLogin();
        }} className="space-y-4">
          <div>
            <input
              id="login-username-input"
              type="text"
              value={username}
              onChange={(e) => {
                // Preserve original casing during typing
                setUsername(e.target.value)
              }}
              // Preserve original casing but trim spaces when the user leaves the field
              onBlur={(e) => setUsername(e.target.value.trim())}
              placeholder="Email"
              className="w-full px-3 py-2 text-[16px] border border-border rounded-input text-body focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </div>

          <div className="relative">
            <div className="relative">
              <input
                id="login-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-3 py-2 text-[16px] border border-border rounded-input text-body focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
              <button
                id="login-password-toggle"
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeOffIcon className="h-5 w-5 text-text-secondary" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-text-secondary" />
                )}
              </button>
            </div>
            {(errors.username || errors.password) && (
              <p id="login-error" className="text-small text-red-600 mt-1 text-end">{errors.username || errors.password}</p>
            )}
          </div>

          <div>
            <div className="flex justify-end">
              <button
                id="login-forgot-password"
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-[16px] font-[500] text-text-secondary hover:text-primary underline"
              >
                Forgot Password
              </button>
            </div>

            <label className="flex items-center">
              <input
                id="login-remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => {
                  setRememberMe(e.target.checked);
                  if (!e.target.checked) {
                    // Clear stored username only
                    localStorage.removeItem('rememberedUsername');
                    localStorage.setItem('rememberMe', 'false');
                    // Clear form fields
                    setUsername('');
                    setPassword('');
                  }
                }}
                className="custom-checkbox"
              />
              <span className="ml-2 text-[16px] font-[500] text-text-secondary">Remember me</span>
            </label>
          </div>

          <button
            id="login-submit"
            type="submit"
            className="w-full btn-primary"
            disabled={isLoggingIn}
          >
            {isLoggingIn ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>

      {/* 2FA Dialog */}
      <Dialog
        isOpen={showOtpDialog}
        onClose={() => setShowOtpDialog(false)}
        width="w-[450px]"
        padding="px-10 py-10"
        className="z-[60]"
        id="otp-dialog"
      >
        <div className="text-center">
          <h3 className="text-heading font-heading mb-2" id="otp-dialog-heading">Two Factor Authentication Required</h3>
          <p className="text-text-secondary text-body mb-8" id="otp-dialog-message">Would you like to receive an OTP to verify your identity?</p>

          <div className="flex justify-center gap-4">
            <button
              id="otp-cancel-button"
              onClick={() => setShowOtpDialog(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              id="otp-send-button"
              onClick={handleGenerateOtp}
              className="btn-primary"
              disabled={isGeneratingOtp}
            >
              {isGeneratingOtp ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
